import { Button, Dropdown, Input, Option, Tab, TabList } from '@fluentui/react-components';
import { Download, Filter, Search } from 'lucide-react';

import { MigrationGroupWithRollup } from '../api/migrationGroups';
import {
  GROUP_STATE_LABEL,
  GroupState,
  collectDestinations,
  countGroupStates,
} from '../lib/groupRollup';
import { NUMERIC, STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-472 — search, state tabs, a destination-tenant filter and CSV export.
//
// ALL OF IT IS CLIENT-SIDE, AND ALL OF IT IS COMPLETE. `GET /v1/migration-groups`
// returns every group in one partition scan (FD-ADR-030 §7), so unlike the
// devices prefix search there is no page boundary and therefore no "of loaded"
// caveat to write. That is a property of THAT endpoint, not a general licence:
// the identity inventory's own filter still says it filters loaded pages,
// because its endpoint pages.
//
// TABS ARE RENDERED ONLY WHEN NON-EMPTY, which is what keeps the row honest on a
// deployment with two states and legible on one with five. `All` is always
// present, and the state counts sum to it — `deriveGroupState` is exhaustive and
// mutually exclusive by precedence, and a test asserts the sum. A tab an
// operator can add up and get a different number from is worse than no tabs.

// Precedence order, matching `deriveGroupState`: the actionable states first.
const TAB_ORDER: GroupState[] = ['expired', 'waveActive', 'notActivated', 'completed', 'noCampaigns'];

const ALL = '__all__';

export interface GroupsToolbarProps {
  /** Every loaded group — the tab and tenant options are derived from this. */
  allGroups: MigrationGroupWithRollup[];
  /** What the filters produced — the export writes exactly this. */
  visibleCount: number;
  query: string;
  onQueryChange: (next: string) => void;
  state?: GroupState;
  onStateChange: (next?: GroupState) => void;
  tenantId?: string;
  onTenantChange: (next?: string) => void;
  onExport: () => void;
}

export default function GroupsToolbar({
  allGroups,
  visibleCount,
  query,
  onQueryChange,
  state,
  onStateChange,
  tenantId,
  onTenantChange,
  onExport,
}: GroupsToolbarProps) {
  const counts = countGroupStates(allGroups);
  const tabs = TAB_ORDER.filter((s) => (counts[s] ?? 0) > 0);
  const destinations = collectDestinations(allGroups);
  const selectedTenant = destinations.find((d) => d.tenantId === tenantId);

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg px-3 py-2"
      style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.base }}
    >
      <TabList
        size="small"
        selectedValue={state ?? ALL}
        onTabSelect={(_, d) => onStateChange(d.value === ALL ? undefined : (d.value as GroupState))}
      >
        <Tab value={ALL}>
          All groups <span style={NUMERIC}>({allGroups.length})</span>
        </Tab>
        {tabs.map((s) => (
          <Tab key={s} value={s}>
            {GROUP_STATE_LABEL[s]} <span style={NUMERIC}>({counts[s]})</span>
          </Tab>
        ))}
      </TabList>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={query}
          onChange={(_, d) => onQueryChange(d.value)}
          placeholder="Search groups…"
          aria-label="Search groups by name or description"
          contentBefore={<Search size={16} strokeWidth={1.5} />}
          size="medium"
          style={{ minWidth: '220px' }}
        />

        {/* Only offered when there is something to choose between. On a
            single-destination deployment — the common case — a dropdown with
            one option is a control that cannot change anything. */}
        {destinations.length > 1 && (
          <Dropdown
            size="medium"
            aria-label="Filter by destination tenant"
            placeholder="All tenants"
            button={
              <span className="inline-flex items-center gap-2">
                <Filter size={16} strokeWidth={1.5} />
                {selectedTenant?.label ?? 'All tenants'}
              </span>
            }
            selectedOptions={[tenantId ?? ALL]}
            onOptionSelect={(_, d) =>
              onTenantChange(d.optionValue === ALL ? undefined : d.optionValue)
            }
          >
            <Option value={ALL}>All tenants</Option>
            {destinations.map((d) => (
              <Option key={d.tenantId} value={d.tenantId}>
                {d.label}
              </Option>
            ))}
          </Dropdown>
        )}

        <Button
          icon={<Download size={16} strokeWidth={1.5} />}
          onClick={onExport}
          disabled={visibleCount === 0}
          // The button sits beside the filters, so it exports what the filters
          // produced. Saying which is the difference between a useful file and
          // one an operator has to re-check by hand.
          title={`Export the ${visibleCount.toLocaleString()} group${visibleCount === 1 ? '' : 's'} shown as CSV`}
        >
          Export
        </Button>
      </div>

      {visibleCount !== allGroups.length && (
        <div className="w-full text-xs" style={{ color: TEXT.secondary }}>
          Showing <span style={NUMERIC}>{visibleCount.toLocaleString()}</span> of{' '}
          <span style={NUMERIC}>{allGroups.length.toLocaleString()}</span> groups.
        </div>
      )}
    </div>
  );
}
